import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardHeader } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Eye, EyeOff, Check, X, Info, Lock } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { API_URL } from '../config/api';
import { LOGO_SRC, LOGO_ALT } from '../config/brand';


const API = API_URL;

const ResetPassword = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const [formData, setFormData] = useState({
    newPassword: '',
    confirmPassword: ''
  });
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [capsLockOn, setCapsLockOn] = useState(false);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  // Password validation
  const passwordMinLength = 8;
  const passwordsMatch = formData.newPassword === formData.confirmPassword;
  const passwordLongEnough = formData.newPassword.length >= passwordMinLength;
  const isValidPassword = passwordLongEnough && passwordsMatch && formData.confirmPassword.length > 0;

  // Detect Caps Lock (with fallback heuristic)
  const detectCapsLock = (event) => {
    if (event.getModifierState && typeof event.getModifierState === 'function') {
      return event.getModifierState('CapsLock');
    }

    const key = event.key;
    if (!key || key.length !== 1) return capsLockOn;

    const isLetter = key.toLowerCase() !== key.toUpperCase();
    if (!isLetter) return capsLockOn;

    const isUpper = key === key.toUpperCase();
    const isShift = event.shiftKey;

    if (isUpper && !isShift) return true;
    if (!isUpper && isShift) return true;
    return false;
  };

  const handleKeyDown = (e) => {
    setCapsLockOn(detectCapsLock(e));
  };

  const handleKeyUp = (e) => {
    setCapsLockOn(detectCapsLock(e));
  };

  useEffect(() => {
    if (!token) {
      setError('Invalid reset link. Please request a new password reset.');
    }
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!isValidPassword) {
      toast.error('Please fix the validation errors');
      return;
    }

    setLoading(true);
    setError('');

    try {
      await axios.post(`${API}/auth/reset-password`, {
        token: token,
        new_password: formData.newPassword
      });
      
      setSuccess(true);
      toast.success('Password updated successfully!');
      
      // Redirect to login after 3 seconds
      setTimeout(() => {
        navigate('/auth');
      }, 3000);
    } catch (err) {
      console.error('Reset password error:', err);
      const errorMsg = err.response?.data?.detail || 'Failed to reset password. Please try again.';
      setError(errorMsg);
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  // Success state
  if (success) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center px-6 py-12">
        <div className="max-w-md w-full">
          <div className="text-center mb-8">
            <img 
              src={LOGO_SRC} 
              alt={LOGO_ALT} 
              className="w-64 mx-auto mb-4"
            />
          </div>

          <Card className="bg-gradient-to-br from-gray-900 via-gray-900 to-gray-800 border-gray-700 shadow-2xl">
            <CardContent className="pt-8 pb-8 text-center">
              <div className="w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <Check className="w-8 h-8 text-green-400" />
              </div>
              <h2 className="text-2xl font-bold text-white mb-2">Password Updated!</h2>
              <p className="text-gray-400 mb-6">
                Your password has been successfully reset. Redirecting you to sign in...
              </p>
              <Button 
                onClick={() => navigate('/auth')}
                className="w-full bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 text-white font-semibold py-4 rounded-xl"
              >
                Sign In Now
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // Error state (invalid/expired token)
  if (error && !token) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center px-6 py-12">
        <div className="max-w-md w-full">
          <div className="text-center mb-8">
            <img 
              src={LOGO_SRC} 
              alt={LOGO_ALT} 
              className="w-64 mx-auto mb-4"
            />
          </div>

          <Card className="bg-gradient-to-br from-gray-900 via-gray-900 to-gray-800 border-gray-700 shadow-2xl">
            <CardContent className="pt-8 pb-8 text-center">
              <div className="w-16 h-16 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <X className="w-8 h-8 text-red-400" />
              </div>
              <h2 className="text-2xl font-bold text-white mb-2">Invalid Link</h2>
              <p className="text-gray-400 mb-6">{error}</p>
              <Button 
                onClick={() => navigate('/auth')}
                className="w-full bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 text-white font-semibold py-4 rounded-xl"
              >
                Back to Sign In
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white flex items-center justify-center px-6 py-12">
      <div className="max-w-md w-full">
        <div className="text-center mb-8">
          <img 
            src={LOGO_SRC} 
            alt={LOGO_ALT} 
            className="w-64 mx-auto mb-4"
          />
        </div>

        <Card className="bg-gradient-to-br from-gray-900 via-gray-900 to-gray-800 border-gray-700 shadow-2xl">
          <CardHeader className="text-center pb-4">
            <div className="w-16 h-16 bg-orange-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <Lock className="w-8 h-8 text-orange-400" />
            </div>
            <h2 className="text-2xl font-bold text-white mb-2">Create New Password</h2>
            <p className="text-gray-400">Enter your new password below</p>
          </CardHeader>
          
          <CardContent className="space-y-4">
            {error && (
              <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3 text-red-400 text-sm">
                {error}
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
              {/* New Password */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">New Password</label>
                <div className="relative">
                  <input
                    type={showNewPassword ? "text" : "password"}
                    value={formData.newPassword}
                    onChange={(e) => setFormData({...formData, newPassword: e.target.value})}
                    onKeyDown={handleKeyDown}
                    onKeyUp={handleKeyUp}
                    className="w-full px-4 py-3 pr-12 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-orange-500"
                    placeholder="••••••••"
                    required
                    minLength={passwordMinLength}
                  />
                  <button
                    type="button"
                    onClick={() => setShowNewPassword(!showNewPassword)}
                    aria-label={showNewPassword ? "Hide password" : "Show password"}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-200 focus:outline-none focus:text-orange-400 transition-colors"
                  >
                    {showNewPassword ? (
                      <EyeOff className="w-5 h-5" />
                    ) : (
                      <Eye className="w-5 h-5" />
                    )}
                  </button>
                </div>
                {/* Password validation feedback */}
                <div className="mt-2 space-y-1">
                  <p className={`text-xs flex items-center gap-1 ${passwordLongEnough ? 'text-green-400' : 'text-gray-500'}`}>
                    {passwordLongEnough ? <Check className="w-3 h-3" /> : <Info className="w-3 h-3" />}
                    At least {passwordMinLength} characters
                  </p>
                </div>
              </div>

              {/* Confirm Password */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Confirm Password</label>
                <div className="relative">
                  <input
                    type={showConfirmPassword ? "text" : "password"}
                    value={formData.confirmPassword}
                    onChange={(e) => setFormData({...formData, confirmPassword: e.target.value})}
                    onKeyDown={handleKeyDown}
                    onKeyUp={handleKeyUp}
                    className="w-full px-4 py-3 pr-12 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-orange-500"
                    placeholder="••••••••"
                    required
                    minLength={passwordMinLength}
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    aria-label={showConfirmPassword ? "Hide password" : "Show password"}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-200 focus:outline-none focus:text-orange-400 transition-colors"
                  >
                    {showConfirmPassword ? (
                      <EyeOff className="w-5 h-5" />
                    ) : (
                      <Eye className="w-5 h-5" />
                    )}
                  </button>
                </div>
                {/* Password match feedback */}
                {formData.confirmPassword.length > 0 && (
                  <p className={`mt-2 text-xs flex items-center gap-1 ${passwordsMatch ? 'text-green-400' : 'text-red-400'}`}>
                    {passwordsMatch ? <Check className="w-3 h-3" /> : <X className="w-3 h-3" />}
                    {passwordsMatch ? 'Passwords match' : 'Passwords do not match'}
                  </p>
                )}
              </div>

              {/* Caps Lock Warning */}
              {capsLockOn && (
                <p className="text-xs text-yellow-400 flex items-center gap-1">
                  <Info className="w-3 h-3" />
                  Caps Lock is on
                </p>
              )}

              <Button 
                type="submit"
                disabled={loading || !isValidPassword}
                className="w-full bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 text-white font-semibold py-6 rounded-xl shadow-lg transform hover:scale-105 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
              >
                {loading ? 'Updating Password...' : 'Reset Password'}
              </Button>
            </form>

            <div className="text-center pt-4">
              <button
                type="button"
                onClick={() => navigate('/auth')}
                className="text-sm text-orange-400 hover:text-orange-300 font-medium"
              >
                ← Back to Sign In
              </button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default ResetPassword;
